import { differenceInMinutes, parse } from 'date-fns';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'src/components/Button';
import { MultiSelectSearchField } from 'src/components/MultiSelectSearchField';
import Select from 'src/components/Select';
import SelectField from 'src/components/SelectField';
import {
  useSessionActivityQuery,
  useSessionOutcomeQuery,
  useSessionParticipationQuery,
} from 'src/dictionaries/dictionariesApi';
import { IconPlus, IconTrash } from 'src/icons';
import { StudentGoal } from 'src/pages/single-student/singleStudent.types';

import { useLazyGetStudentGoalsQuery } from '../scheduleApi';
import { FormActions, SeitGoal } from './RecordSessionContext';

interface SeitGoalForForm extends Omit<SeitGoal, 'activity'> {
  activity?: string[];
}

type SeitGoalsFormProps = {
  dispatch: Dispatch<FormActions>;
  goals: SeitGoalForForm[];
  error?: string;
  disabled?: boolean;
  startTime?: string;
  finishTime?: string;
  selectedStudentId?: string;
  requiredGoals?: number;
};

export const SeitGoalsForm = ({
  dispatch,
  goals,
  error,
  disabled,
  startTime,
  finishTime,
  selectedStudentId,
  requiredGoals: requiredGoalsProp,
}: SeitGoalsFormProps) => {
  const [fetchGoals, { data: availableGoals = [] as StudentGoal[] }] =
    useLazyGetStudentGoalsQuery();
  const [isAddingNewGoal, setIsAddingNewGoal] = useState(goals.length === 0);
  const { data: sessionActivity } = useSessionActivityQuery();
  const { data: sessionParticipation } = useSessionParticipationQuery();
  const { data: sessionOutcome } = useSessionOutcomeQuery();

  const handleGoalChange = useCallback(
    (index: number, field: keyof SeitGoalForForm, value: string | string[]) => {
      dispatch({ type: 'updateSeitGoal', payload: { index, field, value } });
    },
    [dispatch],
  );

  const handleAddGoal = useCallback(() => {
    setIsAddingNewGoal(true);
  }, []);

  const handleRemoveGoal = useCallback(
    (index: number) => {
      dispatch({ type: 'removeSeitGoal', payload: { index } });
      if (goals.length === 1) {
        setIsAddingNewGoal(true);
      }
    },
    [dispatch, goals.length],
  );

  const requiredGoals = useMemo(() => {
    if (requiredGoalsProp !== undefined) {
      return requiredGoalsProp;
    }

    if (!startTime || !finishTime) return 0;
    try {
      const baseDate = new Date(1970, 0, 1);
      const start = parse(startTime, 'HH:mm', baseDate);
      const finish = parse(finishTime, 'HH:mm', baseDate);
      const durationInMinutes = differenceInMinutes(finish, start);
      return Math.max(1, Math.ceil(durationInMinutes / 30));
    } catch (e) {
      return 0;
    }
  }, [startTime, finishTime, requiredGoalsProp]);

  const isCurrentGoalComplete = useMemo(() => {
    const lastGoal = goals[goals.length - 1];
    if (!lastGoal) return true;
    return !!(lastGoal.activity?.length && lastGoal.participation && lastGoal.outcome);
  }, [goals]);

  useEffect(() => {
    if (selectedStudentId && !disabled) {
      fetchGoals(selectedStudentId);
    }
  }, [selectedStudentId, disabled, fetchGoals]);

  const goalsIds = goals.map((goal) => goal.id);
  const leftGoals = availableGoals.filter((goal) => !goalsIds.includes(goal.id));

  const handleGoalSelect = useCallback(
    (selectedGoalId: string) => {
      const selectedGoal = availableGoals.find((g) => g.id === selectedGoalId);
      if (selectedGoal) {
        dispatch({
          type: 'addSeitGoal',
          payload: { id: selectedGoalId, name: selectedGoal.definition.name },
        });
        setIsAddingNewGoal(false);
      }
    },
    [availableGoals, dispatch],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-start justify-between gap-6">
        <p className="text-neutral-200 typography-heading-xs">Goals</p>
        {requiredGoals > 0 && (
          <p className="rounded-md bg-secondary-900 px-2 py-1 text-secondary-300 typography-loud-xs">
            {requiredGoals} goal(s) required based on session length.
          </p>
        )}
      </div>

      {goals.map((goal, index) => (
        <div
          key={goal.id || index}
          className="space-y-3 rounded-md border border-neutral-800 bg-neutral-950 p-4"
        >
          <div className="flex items-center justify-between">
            <p className="text-neutral-100 typography-loud-sm">{goal.name}</p>
            {!disabled && (
              <button onClick={() => handleRemoveGoal(index)}>
                <IconTrash className="h-5 w-6 text-neutral-400" />
              </button>
            )}
          </div>
          <MultiSelectSearchField
            options={
              sessionActivity?.map((activity) => ({
                value: activity.key,
                label: activity.value,
              })) || []
            }
            value={goal.activity || []}
            onChange={(selectedActivityValues) =>
              handleGoalChange(index, 'activity', selectedActivityValues)
            }
            disabled={disabled}
            placeholder="Activity"
            searchPlaceholder="Search"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SelectField
              options={
                sessionParticipation?.map((participation) => ({
                  value: participation.key,
                  label: participation.value,
                })) || []
              }
              value={goal.participation}
              onValueChange={(value) => handleGoalChange(index, 'participation', value)}
              disabled={disabled}
              placeholder="Participation"
            />
            <SelectField
              options={
                sessionOutcome?.map((outcome) => ({
                  value: outcome.key,
                  label: outcome.value,
                })) || []
              }
              value={goal.outcome}
              onValueChange={(value) => handleGoalChange(index, 'outcome', value)}
              disabled={disabled}
              placeholder="Outcome"
            />
          </div>
        </div>
      ))}

      {isAddingNewGoal && !disabled && leftGoals.length > 0 && (
        <Select
          options={leftGoals.map((g) => ({ value: g.id, label: g.definition.name }))}
          onValueChange={handleGoalSelect}
          disabled={disabled}
          placeholder="Select goal"
        />
      )}

      {!disabled && leftGoals.length > 0 && !isAddingNewGoal && isCurrentGoalComplete && (
        <Button preset="tertiary" size="sm" onClick={handleAddGoal} Icon={IconPlus}>
          Add Goal
        </Button>
      )}
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
};
